<script setup lang="ts">
import { ButtonComponent, Card, FlexComponent, TypographyComponent } from '@metapic-apps/shared'
import type { StripeCardElement } from '@stripe/stripe-js'
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'

import { useUpdatePaymentCard } from '@/api/advertiser.api'
import EditIcon from '@/assets/icons/edit.svg?component'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { stripe } from '@/lib/helpers/stripe'
import type { AdvertiserType } from '@/lib/types/advertiser.types'
import { useErrorStore } from '@/stores/error.stores'

const props = defineProps<{
  advertiserId: string
  card?: AdvertiserType['card']
}>()

const cardElementRef = ref<HTMLDivElement>()
const isEditing = ref(!props.card)
let cardElement: StripeCardElement | undefined
const errorStore = useErrorStore()

const { loading, error, updateCard } = useUpdatePaymentCard(computed(() => props.advertiserId))

const brandLabel = computed(() => props.card?.brand?.toUpperCase() ?? 'Card')
const maskedNumber = computed(() => (props.card ? `**** **** **** ${props.card.last4}` : ''))

const mountCardElement = () => {
  if (cardElement || !cardElementRef.value) {
    return
  }

  try {
    const elements = stripe.elements()
    cardElement = elements.create('card', { hidePostalCode: true })
    cardElement.mount(cardElementRef.value)
  } catch (e) {
    error.value = e as Error
    errorStore.setError(handleErrorMessage(e))
    isEditing.value = false
  }
}

const startEditing = async () => {
  isEditing.value = true
  await nextTick()
  mountCardElement()
}

const save = async () => {
  if (!isEditing.value) {
    return
  }
  if (!cardElement) {
    mountCardElement()
  }
  if (!cardElement) {
    return
  }
  await updateCard(cardElement)
  if (error.value) {
    return
  }
  cardElement.destroy()
  cardElement = undefined
  isEditing.value = false
}

onBeforeUnmount(() => {
  cardElement?.destroy()
})

if (isEditing.value) {
  void nextTick(mountCardElement)
}
</script>

<template>
  <Card title="Payment" title-size="small" class="payment-settings">
    <div class="payment-settings__card">
      <TypographyComponent variant="title-large">{{ brandLabel }}</TypographyComponent>
      <FlexComponent direction="row" align="center" :gap="16" class="payment-settings__card-row">
        <TypographyComponent v-if="!isEditing" variant="body-large">{{ maskedNumber }}</TypographyComponent>
        <div v-else ref="cardElementRef" class="payment-settings__stripe-input"></div>
        <ButtonComponent
          v-if="!isEditing"
          is-icon
          variant="transparent"
          class="payment-settings__edit-button"
          aria-label="Edit card"
          @click="startEditing"
        >
          <EditIcon />
        </ButtonComponent>
      </FlexComponent>
    </div>

    <template #footer>
      <ButtonComponent variant="accent" :is-loading="loading" :is-disabled="!isEditing" class="payment-settings__button" @click="save">
        Save
      </ButtonComponent>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.payment-settings {
  &__card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 280px;
    padding: 16px;
    border: 1px solid var(--secondary-8);
    border-radius: 16px;
  }

  &__card-row {
    width: 100%;
  }

  &__stripe-input {
    flex: 1;
  }

  &__button {
    align-self: flex-start;
  }

  &__edit-button {
    &:hover {
      background: var(--button-hover);
    }
  }
}
</style>
